import { useCallback, useRef } from 'react'
import { Transition } from 'react-transition-group'

import Eye, { EyeRef } from '@/components/Eye/Eye'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import useTracking from '@/composables/useTracking'

import LoadingIntro from './LoadingIntro'

export type LoadingCatchProps = React.HTMLProps<HTMLDivElement> & {
  isVisible?: boolean
}

export default function LoadingCatch({ className, isVisible = false }: LoadingCatchProps) {
  const { trackImpression, trackClick } = useTracking()
  const { actor, persona, scenarioUnitName } = useAppState()
  const { gsap } = useGSAP()

  const eyeRef = useRef<EyeRef>(null)
  const elementRef = useRef<HTMLDivElement>(null)
  const backgroundShape1Ref = useRef<SVGSVGElement>(null)
  const backgroundShape2Ref = useRef<SVGSVGElement>(null)
  const backgroundShape3Ref = useRef<SVGSVGElement>(null)

  const onStart = useCallback(() => {
    trackClick({
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitLocation: 'tyce',
      unitVariation: 'intro_headphones',
      clickType: 'button',
      buttonName: 'got_it',
    })

    const ctx = gsap.context(() => {
      if (elementRef.current && eyeRef.current) {
        gsap
          .timeline()
          .add(() => eyeRef.current?.close({ blink: true }), 0)
          .add(() => {
            actor.send({ type: 'NEXT' })
          }, 0.75)
      }

      const bgTargets = [backgroundShape1Ref.current, backgroundShape2Ref.current, backgroundShape3Ref.current].filter(
        Boolean
      )
      if (bgTargets.length) {
        gsap.to(bgTargets, {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.out',
        })
      }
    })

    return () => ctx.kill()
  }, [
    gsap,
    eyeRef,
    elementRef,
    backgroundShape1Ref,
    backgroundShape2Ref,
    backgroundShape3Ref,
    actor,
    persona,
    trackClick,
    scenarioUnitName,
  ])

  const onEnter = useCallback(() => {
    trackImpression({
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitLocation: 'tyce',
      unitVariation: 'intro_headphones',
    })

    const ctx = gsap.context(() => {
      if (elementRef.current && eyeRef.current) {
        gsap.timeline().add(() => eyeRef.current?.open({}), 0.1)
      }
    })

    if (backgroundShape1Ref.current) {
      gsap.to(backgroundShape1Ref.current, {
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out',
        delay: 0.3,
      })
    }

    if (backgroundShape2Ref.current) {
      gsap.to(backgroundShape2Ref.current, {
        opacity: 1,
        duration: 0.1,
        ease: 'power2.out',
        delay: 0.6,
      })
      gsap.to(backgroundShape2Ref.current, {
        rotate: -45,
        duration: 1.5,
        ease: 'linear',
      })
    }

    if (backgroundShape3Ref.current) {
      gsap.to(backgroundShape3Ref.current, {
        opacity: 1,
        duration: 0.1,
        ease: 'power2.out',
        delay: 0.9,
      })
      gsap.to(backgroundShape3Ref.current, {
        rotate: 45,
        duration: 1.5,
        ease: 'linear',
      })
    }

    return () => ctx.kill()
  }, [
    gsap,
    eyeRef,
    elementRef,
    backgroundShape1Ref,
    backgroundShape2Ref,
    backgroundShape3Ref,
    trackImpression,
    persona,
    scenarioUnitName,
  ])

  return (
    <Transition
      in={isVisible}
      appear
      nodeRef={elementRef}
      mountOnEnter
      unmountOnExit
      onEnter={onEnter}
      timeout={{
        enter: 400,
        exit: 0,
      }}
    >
      <div
        data-testid='loading-catch'
        ref={elementRef}
        className={`LoadingCatch relative grid pointer-events-none ${className ? className : ''}`}
      >
        <svg
          ref={backgroundShape1Ref}
          className='w-[23px] md:w-[40px] absolute left-[70%] top-[10%] opacity-0'
          viewBox='0 0 41 41'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M22.7853 3.93242L11.2772 0.37977C5.15923 -1.5082 -0.706914 3.93905 0.717925 10.1855L3.3975 21.9355L6.07708 33.6856C7.50192 39.932 15.1486 42.292 19.8417 37.9346L28.6702 29.7372L37.4986 21.5387C42.1918 17.1813 40.4113 9.37304 34.2933 7.48396L22.7853 3.93131L22.7853 3.93242Z'
            fill='#FED1C8'
          />
        </svg>

        <svg
          className='w-[65px] md:w-[104px] absolute left-[80%] top-[15%] md:top-[25%] z-1 opacity-0'
          viewBox='0 0 104 104'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          ref={backgroundShape2Ref}
        >
          <path
            d='M103.84 60.9898C103.411 54.9777 100.959 49.2238 96.8556 44.7244H96.857C93.1931 40.4712 91.5823 38.5859 90.9129 36.148C90.6505 34.4295 90.7168 32.3049 90.8182 28.9862C90.8182 28.9822 90.8182 28.9781 90.8182 28.9741C90.8182 28.9432 90.8195 28.9163 90.8209 28.8854H90.8195C90.9169 25.9136 90.5166 22.966 89.6591 20.1597C87.6601 12.62 82.4071 5.99474 74.7143 2.47973C64.5044 -2.18365 52.8907 -0.0429036 45.0531 7.00057C39.9389 11.3412 34.2856 13.4286 28.0931 13.2628C28.0931 13.2628 28.0836 13.2628 28.0795 13.2628C28.0498 13.2628 28.0214 13.2614 27.9916 13.2601C17.4234 12.9723 7.19867 18.8432 2.49882 28.9795C-0.31027 35.0387 -0.668676 41.594 1.0111 47.5496C2.0755 51.8109 4.17589 55.7939 7.16892 59.1086H7.16757C11.5645 64.2641 12.9873 65.9557 13.3633 69.3268C13.4715 70.8316 13.4228 72.6872 13.3578 75.2609C13.3578 75.265 13.3578 75.269 13.3578 75.2731C13.3578 75.304 13.3565 75.3309 13.3551 75.3618H13.3565C13.3376 76.053 13.3457 76.7428 13.3795 77.4313C13.558 87.4344 19.3466 96.9494 29.0953 101.462C39.2835 106.178 50.9066 104.095 58.7794 97.0906C63.9152 92.7751 69.5794 90.7159 75.7719 90.9131C75.7719 90.9131 75.7813 90.9131 75.7854 90.9131C75.8152 90.9131 75.8436 90.9145 75.8733 90.9158C86.4402 91.256 96.6947 85.4362 101.445 75.3228C103.626 70.6796 104.362 65.7365 103.838 60.9898H103.84Z'
            fill='#4EA4BD'
          />
        </svg>

        <svg
          className='w-[53px] md:w-[71px] absolute left-[15%] top-[80%] md:top-[75%] z-1 opacity-0'
          viewBox='0 0 71 71'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          ref={backgroundShape3Ref}
        >
          <path
            d='M46.6573 69.9655C45.0784 71.7386 42.1715 71.1377 41.4258 68.8834L36.2774 53.3336L36.2774 53.3346L33.1602 43.9186C32.9353 44.4011 32.7117 44.8839 32.4844 45.3649C32.2036 45.96 31.9164 46.5544 31.6309 47.1481C31.3273 47.781 31.0209 48.4126 30.7188 49.0455C30.488 49.5299 30.2631 50.0173 30.0323 50.5016C29.7302 51.1362 29.4223 51.7664 29.1202 52.401C28.8804 52.9034 28.6476 53.4117 28.4092 53.9157C28.0904 54.5897 27.7687 55.2626 27.4483 55.9352C27.2373 56.3753 27.0269 56.8158 26.8175 57.2575C26.4865 57.9513 26.1582 58.6486 25.8272 59.3424C25.6329 59.7508 25.4312 60.1547 25.2384 60.5631C24.8877 61.3054 24.5397 62.0507 24.1905 62.7946C23.9719 63.2576 23.7519 63.7211 23.5333 64.1842C23.2008 64.8856 22.8723 65.5884 22.5352 66.2867C22.3956 66.5781 22.2681 66.877 22.086 67.1442C21.4939 68.011 20.5134 68.377 19.2139 68.2936C18.959 68.2769 18.6946 68.1957 18.4337 68.1471C18.3715 68.1365 18.3074 68.1346 18.2452 68.1285L17.4415 67.8082L17.4434 67.8063C17.3585 67.7486 17.278 67.685 17.1885 67.6334C16.5919 67.2949 16.1751 66.8338 15.794 66.3434C15.2915 65.6967 14.7776 65.0554 14.2706 64.4117C13.7043 63.6921 13.145 62.9697 12.5772 62.2516C12.0109 61.5335 11.4345 60.8179 10.8682 60.0983C10.1349 59.1676 9.41014 58.234 8.67683 57.3033C7.96633 56.4031 7.24528 55.5073 6.53327 54.6071C5.94267 53.8601 5.36302 53.1079 4.77546 52.361C4.22436 51.658 3.65621 50.963 3.11726 50.2555C2.88497 49.9503 2.66145 49.6284 2.5157 49.3004C2.26832 48.7463 2.20754 48.1934 2.36238 47.6666C2.36805 47.6439 2.33669 47.6093 2.3321 47.5836C2.35184 47.5457 2.40481 47.5196 2.41999 47.4801C2.67504 46.8137 3.20201 46.3568 3.93366 46.0455C4.8188 45.669 5.69522 45.2788 6.57429 44.8932C7.37741 44.541 8.17937 44.1868 8.98249 43.8346C9.88874 43.4369 10.7903 43.0328 11.7042 42.6442C12.196 42.4362 12.6743 42.2102 13.1632 41.9977C13.854 41.6986 14.5392 41.3891 15.2315 41.0885C16.5584 40.5116 17.8915 39.9404 19.2169 39.3619C20.1019 38.9763 20.9794 38.5783 21.8614 38.1881C22.6478 37.8405 23.4377 37.4962 24.2257 37.15C24.8875 36.8601 25.5525 36.5714 26.2159 36.2799C26.8815 35.988 27.5463 35.6969 28.211 35.4049C27.3624 35.0595 26.5138 34.7105 25.6651 34.3629C25.0519 34.1125 24.4366 33.8634 23.8233 33.6129C23.093 33.3153 22.3636 33.0171 21.6348 32.7164C20.8182 32.3795 20.0043 32.0367 19.1846 31.7028C17.958 31.2048 16.7283 30.7129 15.5001 30.2164C14.8609 29.9568 14.2242 29.6894 13.585 29.4313C13.1328 29.2492 12.6882 29.0518 12.2344 28.8727C11.3905 28.5403 10.5538 28.1913 9.71589 27.8483C8.97346 27.5446 8.22882 27.2388 7.4864 26.9352C6.67273 26.6027 5.86224 26.2654 5.04402 25.942C4.3684 25.6748 3.84005 25.2575 3.503 24.6305C3.48786 24.6017 3.45306 24.5805 3.42878 24.5563L3.41023 24.5241C3.41336 24.4999 3.42804 24.4729 3.41902 24.4547C3.1928 23.9552 3.14325 23.4221 3.25105 22.8785C3.31481 22.5583 3.44389 22.2397 3.58503 21.9362C3.91146 21.2332 4.26502 20.5403 4.60359 19.8434C4.96486 19.1011 5.31697 18.3542 5.67976 17.6119C6.11702 16.7177 6.56379 15.8261 7.00105 14.9303C7.45188 14.0059 7.89353 13.0784 8.34285 12.1539C8.69047 11.4389 9.04741 10.7283 9.39656 10.0133C9.74424 9.29822 10.086 8.58095 10.4337 7.86585C10.7449 7.22524 11.0635 6.58904 11.3702 5.9469C11.6025 5.45957 11.8718 4.99823 12.3136 4.64905C12.3803 4.59594 12.4365 4.53047 12.4971 4.47132L12.4971 4.47718C12.7035 4.36337 12.9119 4.24974 13.1182 4.1344C13.1697 4.12535 13.223 4.12167 13.2745 4.10803C13.4854 4.05035 13.6934 3.96154 13.9073 3.93421C14.9913 3.79909 15.8871 4.10851 16.546 4.91468C16.7494 5.1636 16.9121 5.44572 17.0821 5.71741C17.4935 6.37327 17.9003 7.0341 18.3087 7.693C18.5789 8.12874 18.8495 8.56293 19.1182 8.99866C19.5494 9.69704 19.9785 10.3989 20.4112 11.0973C20.6479 11.4812 20.8927 11.8604 21.1309 12.2428C21.5378 12.8956 21.9418 13.549 22.3487 14.2018C22.6068 14.6163 22.865 15.031 23.1231 15.444C23.5163 16.0755 23.9131 16.7074 24.3048 17.3405C24.5992 17.814 24.8873 18.2923 25.1817 18.7643C25.5522 19.3594 25.9285 19.9533 26.2989 20.5485C26.5827 21.0038 26.8608 21.4619 27.1446 21.9157C27.5151 22.5108 27.8908 23.1052 28.2628 23.6989C28.6118 24.2558 28.9626 24.8127 29.3087 25.3727C29.6457 25.9162 29.978 26.4634 30.3135 27.0084C30.5944 27.4654 30.8754 27.9222 31.1593 28.3776C31.1976 28.4391 31.236 28.5007 31.2745 28.5621L31.668 19.3229L32.3643 2.95765C32.4661 0.584622 35.0975 -0.787845 37.1016 0.485967L50.9249 9.2721L64.7491 18.0582C66.753 19.332 66.6271 22.2968 64.5216 23.3961L50.001 30.9752L41.7872 35.2643L50.7217 37.111L66.7618 40.4264C69.0877 40.9077 70.0202 43.7253 68.4415 45.4987L57.5499 57.7311L46.6573 69.9655Z'
            fill='#06748D'
          />
        </svg>

        <Eye
          ref={eyeRef}
          className={`relative ${className ? className : ''}`}
          defaultOpen={false}
          isLargeMobile
          clipOnMobile
          childrenBackground={<div className='absolute top-0 left-0 w-full h-full bg-dark-cream' />}
        >
          <div className='relative w-full h-full flex items-center justify-center'>
            <LoadingIntro onStart={onStart} />
          </div>
        </Eye>
      </div>
    </Transition>
  )
}
